import { format, isValid } from 'date-fns';
import type { DateLocale } from '@/hooks/useDateLocale';

/**
 * Every date string the user reads comes from here.
 *
 * Formatting used to be written per call site, which is why weekdays and month
 * names stayed English in half the app however the language was set: each new
 * date needed someone to remember to pass a locale, and a hardcoded `'en-GB'`
 * or a bare `format(d, 'MMM d')` looked perfectly fine in review. The locale is
 * now baked into the formatter rather than supplied at the call site, so the
 * failure mode is gone instead of being caught by vigilance.
 *
 * Use `useDateFormat()` in components. `createDateFormatters` is for the few
 * plain functions that cannot call a hook and take a locale as an argument.
 */

// ---------------------------------------------------------------------------
// Machine formats. NOT localised, deliberately.
//
// These are lookup keys and form values -- a booking map keyed by day, the
// `yyyy-MM-dd` an <input type="date"> expects. Localising them would silently
// break the lookups, so they live apart from the display formatters and are
// the one correct use of a bare `format()` call.
// ---------------------------------------------------------------------------

export const DATE_KEY = 'yyyy-MM-dd';
export const TIME_KEY = 'HH:mm';

export const toDateKey = (value: Date): string => format(value, DATE_KEY);
export const toTimeKey = (value: Date): string => format(value, TIME_KEY);

// ---------------------------------------------------------------------------
// Display formats.
// ---------------------------------------------------------------------------

export type DateInput = Date | string | number | null | undefined;

const coerce = (value: DateInput): Date | null => {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? date : null;
};

export interface DateFormatters {
  /** Single letter or two, for a seven-column grid header: `Mo` / `P`. */
  weekdayNarrow(value: DateInput): string;
  /** Abbreviated weekday: `Mon` / `pirmd.`. */
  weekdayShort(value: DateInput): string;
  /** Day number alone: `4`. */
  dayOfMonth(value: DateInput): string;
  /** `4 Mar 2026` — day first, which is what both locales expect. */
  date(value: DateInput): string;
  /** `Wed, 4 Mar` — no year, for things happening around now. */
  dateWithWeekday(value: DateInput): string;
  /** `Wednesday, 4 March 2026`. */
  dateLong(value: DateInput): string;
  /** `4 March` — no year, for grouping items from the recent past. */
  dayMonth(value: DateInput): string;
  /** `March 2026`, for a calendar heading. */
  monthYear(value: DateInput): string;
  /** `4 Mar - 10 Mar 2026`, collapsing the year when both ends share it. */
  dateRange(start: DateInput, end: DateInput): string;
  /** 24-hour `14:30`. Deliberately not localised: the product is European. */
  time(value: DateInput): string;
  /** `4 Mar 2026, 14:30`. */
  dateTime(value: DateInput): string;
}

export function createDateFormatters(locale: DateLocale): DateFormatters {
  const opts = { locale: locale.dateFns };
  const fmt =
    (pattern: string) =>
    (value: DateInput): string => {
      const date = coerce(value);
      return date ? format(date, pattern, opts) : '';
    };

  const time = fmt(TIME_KEY);
  const date = fmt('d MMM yyyy');

  return {
    weekdayNarrow: fmt('EEEEEE'),
    weekdayShort: fmt('EEE'),
    dayOfMonth: fmt('d'),
    date,
    dateWithWeekday: fmt('EEE, d MMM'),
    dateLong: fmt('EEEE, d MMMM yyyy'),
    dayMonth: fmt('d MMMM'),
    monthYear: fmt('LLLL yyyy'),
    time,
    dateTime(value) {
      const parsed = coerce(value);
      return parsed ? `${date(parsed)}, ${time(parsed)}` : '';
    },
    dateRange(startValue, endValue) {
      const start = coerce(startValue);
      const end = coerce(endValue);
      if (!start || !end) return date(start ?? end);
      const sameYear = start.getFullYear() === end.getFullYear();
      const left = sameYear
        ? format(start, 'd MMM', opts)
        : format(start, 'd MMM yyyy', opts);
      return `${left} - ${date(end)}`;
    },
  };
}
